/**
 * CommandPalette - Cmd/Ctrl+K quick-actions palette
 *
 * A controlled, keyboard-driven command palette in the spirit of Raycast /
 * Linear / Figma quick-actions. Renders a centered floating panel with a
 * search input and grouped, filterable results.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Search } from 'lucide-react'
import '../styles/command-palette.css'

export interface Command {
  id: string
  label: string
  group?: string // e.g. 'Edit', 'Insert', 'View', 'Arrange', 'Export'
  keywords?: string // extra search terms
  shortcut?: string // display only, e.g. 'Ctrl+Z'
  icon?: React.ReactNode // a lucide icon element
  disabled?: boolean
  run: () => void
}

export interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
  commands: Command[]
}

const DEFAULT_GROUP = 'Actions'

function matchesQuery(cmd: Command, query: string): boolean {
  if (!query) return true
  const haystack = `${cmd.label} ${cmd.group ?? ''} ${cmd.keywords ?? ''}`.toLowerCase()
  return haystack.includes(query)
}

function CommandPaletteComponent({ isOpen, onClose, commands }: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  // Reset query + autofocus whenever the palette opens.
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      // Defer focus to the next frame so the element is mounted.
      const id = requestAnimationFrame(() => inputRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [isOpen])

  // Filtered commands (disabled stay visible but unselectable).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return commands.filter((cmd) => matchesQuery(cmd, q))
  }, [commands, query])

  // Group results, preserving insertion order of groups and commands.
  const groups = useMemo(() => {
    const order: string[] = []
    const map = new Map<string, Command[]>()
    for (const cmd of filtered) {
      const group = cmd.group || DEFAULT_GROUP
      if (!map.has(group)) {
        map.set(group, [])
        order.push(group)
      }
      map.get(group)!.push(cmd)
    }
    return order.map((group) => ({ group, items: map.get(group)! }))
  }, [filtered])

  // Flat list of enabled commands in display order (for keyboard navigation).
  const enabledIds = useMemo(
    () => filtered.filter((c) => !c.disabled).map((c) => c.id),
    [filtered],
  )

  // Reset highlight to the first enabled item whenever the query (or the set of
  // results) changes.
  useEffect(() => {
    setHighlightedId(enabledIds.length > 0 ? enabledIds[0] : null)
  }, [enabledIds])

  const runCommand = useCallback(
    (cmd: Command) => {
      if (cmd.disabled) return
      cmd.run()
      onClose()
    },
    [onClose],
  )

  const moveHighlight = useCallback(
    (direction: 1 | -1) => {
      if (enabledIds.length === 0) return
      setHighlightedId((current) => {
        const currentIndex = current ? enabledIds.indexOf(current) : -1
        const nextIndex =
          (currentIndex + direction + enabledIds.length) % enabledIds.length
        return enabledIds[nextIndex]
      })
    },
    [enabledIds],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveHighlight(1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveHighlight(-1)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const cmd = filtered.find((c) => c.id === highlightedId)
        if (cmd) runCommand(cmd)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    },
    [moveHighlight, filtered, highlightedId, runCommand, onClose],
  )

  // Keep the highlighted item scrolled into view.
  useEffect(() => {
    if (!highlightedId || !listRef.current) return
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-command-id="${CSS.escape(highlightedId)}"]`,
    )
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlightedId])

  if (!isOpen) return null

  const hasResults = filtered.length > 0

  return (
    <div className="command-palette-backdrop" onMouseDown={onClose}>
      <div
        className="command-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="command-palette-search">
          <Search className="command-palette-search-icon" size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            className="command-palette-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a command or search…"
            aria-label="Command palette"
            aria-controls="command-palette-list"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div
          ref={listRef}
          className="command-palette-list"
          id="command-palette-list"
          role="listbox"
          aria-label="Commands"
        >
          {hasResults ? (
            groups.map(({ group, items }) => (
              <div className="command-palette-group" key={group}>
                <div className="command-palette-group-label">{group}</div>
                {items.map((cmd) => {
                  const isHighlighted = cmd.id === highlightedId
                  return (
                    <div
                      key={cmd.id}
                      data-command-id={cmd.id}
                      role="option"
                      aria-selected={isHighlighted}
                      aria-disabled={cmd.disabled || undefined}
                      className={
                        'command-palette-item' +
                        (isHighlighted ? ' highlighted' : '') +
                        (cmd.disabled ? ' disabled' : '')
                      }
                      onMouseEnter={() => {
                        if (!cmd.disabled) setHighlightedId(cmd.id)
                      }}
                      onMouseDown={(e) => {
                        // Prevent the input from losing focus / backdrop close.
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={() => runCommand(cmd)}
                    >
                      {cmd.icon ? (
                        <span className="command-palette-item-icon" aria-hidden="true">
                          {cmd.icon}
                        </span>
                      ) : (
                        <span className="command-palette-item-icon" aria-hidden="true" />
                      )}
                      <span className="command-palette-item-label">{cmd.label}</span>
                      {cmd.shortcut ? (
                        <span className="command-palette-shortcut">{cmd.shortcut}</span>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ))
          ) : (
            <div className="command-palette-empty">No commands found</div>
          )}
        </div>
      </div>
    </div>
  )
}

const CommandPalette = React.memo(CommandPaletteComponent)
CommandPalette.displayName = 'CommandPalette'

export default CommandPalette
